/**
 * React Query hooks for the Commerce catalog read surfaces (Products +
 * Inventory), backed by the product_catalog and inventory_on_hand views.
 *
 *   useCatalogProducts(f)     GET /v1/commerce/catalog/products
 *   useCatalogProduct(code)   GET /v1/commerce/catalog/products/{itemCode}
 *   useCatalogInventory(f, date)              GET /v1/commerce/catalog/inventory
 *   useCatalogInventorySnapshotDates()         GET /v1/commerce/catalog/inventory/snapshot-dates
 *
 * Binary access-grant model (spec 00_access-binary-grants.md, 2026-08-09):
 * Products and Inventory are independently-grantable tools, each its own
 * accessSlug, replacing the shared commerce:read capability. Lists use keyset
 * (cursor) pagination: the response carries a `next_cursor` string fed
 * straight back as the `cursor` query param.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiGet } from './api';
import { API_BASE } from './apiBase';

export const CATALOG_API_BASE = `${API_BASE}/v1/commerce/catalog`;

export function hasCommerceProducts(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('commerce.products');
}

export function hasCommerceInventory(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('commerce.inventory');
}

// product_catalog view unifies both brands ('hammonds' | 'lolli').
export type CatalogBrand = 'hammonds' | 'lolli';

export type ProductRow = {
  brand: string;
  item_code: string;
  description: string | null;
  uom: string | null;
  case_pack: number | null;
  price: string | null;
  on_hand: string | null;
  active: boolean;
};

export type ProductInventoryRow = {
  brand: string;
  warehouse: string;
  // The API is adding a human-readable warehouse name (lolli = store name;
  // hammonds may be null). Optional so a payload without it still renders the
  // bare code. When present, the UI shows "101 · Tulsa".
  warehouse_name?: string | null;
  on_hand: string | null;
  committed: string | null;
  available: string | null;
};

export type ProductDetail = ProductRow & {
  inventory: ProductInventoryRow[];
};

export type InventoryRow = {
  brand: string;
  item_code: string;
  /**
   * Product description from the brand's item master (Sage CI_Item for
   * Hammond's, cp_item_mirror for Lolli). Optional and nullable: the API
   * LEFT JOINs the master, so a stock row whose item has no master record still
   * appears here with null rather than dropping out of the grid.
   */
  description?: string | null;
  warehouse: string;
  warehouse_name?: string | null;
  on_hand: string | null;
  committed: string | null;
  available: string | null;
  /**
   * Average cost per unit and on hand x average cost. Null where the brand holds no cost for
   * the row (common for Hammond's items): show blank, never zero. Optional so an older api
   * that does not send them still types.
   */
  avg_cost?: string | null;
  value?: string | null;
  /** Lolli item department and statement (CounterPoint). Null for Hammond's rows. */
  department?: string | null;
  statement?: string | null;
  /** Last sale day (YYYY-MM-DD), live Lolli rows only. */
  last_sale?: string | null;
};

// -------- products list ------------------------------------------------------

export type ProductFilters = {
  q?: string;
  brand?: CatalogBrand;
  active?: boolean;
};

type ListProductsResponse = {
  products: ProductRow[];
  next_cursor: string | null;
  total_count: number | null;
  limit: number;
};

function buildProductsQs(f: ProductFilters, cursor: string | null): string {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  if (f.brand) p.set('brand', f.brand);
  if (f.active !== undefined) p.set('active', String(f.active));
  p.set('limit', '50');
  if (cursor) p.set('cursor', cursor);
  return p.toString();
}

export function useCatalogProducts(filters: ProductFilters = {}) {
  const query = useInfiniteQuery<ListProductsResponse, Error>({
    queryKey: ['catalog-products', filters],
    queryFn: ({ pageParam }) =>
      apiGet<ListProductsResponse>(
        `${CATALOG_API_BASE}/products?${buildProductsQs(filters, (pageParam as string | null) ?? null)}`,
      ),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 30_000,
    retry: false,
  });
  const rows: ProductRow[] = (query.data?.pages ?? []).flatMap((p) => p.products);
  return {
    rows,
    totalCount: query.data?.pages[0]?.total_count ?? null,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: () => query.fetchNextPage(),
    error: query.error,
  };
}

export function useCatalogProduct(itemCode: string | null | undefined) {
  return useQuery<ProductDetail, Error>({
    queryKey: ['catalog-product', String(itemCode ?? '')],
    queryFn: () => apiGet<ProductDetail>(`${CATALOG_API_BASE}/products/${encodeURIComponent(String(itemCode ?? ''))}`),
    enabled: !!itemCode,
    retry: false,
  });
}

// -------- inventory list -----------------------------------------------------

export type InventoryFilters = {
  q?: string;
  brand?: CatalogBrand;
  warehouse?: string;
  low_stock?: boolean;
  // In-stock-only. Defaults ON server-side, so this is only sent when the user
  // turns it OFF (about 96% of mirror rows carry no quantity and sort to the top
  // by item code, which made the unfiltered default view 50 rows of zeros).
  in_stock?: boolean;
  /** Lolli department / statement (values from useCatalogInventoryFilterOptions). Hammond's rows never match. */
  department?: string;
  statement?: string;
  /**
   * Out of stock, sold in the last year: Lolli rows with nothing on hand and a sale within 365
   * days. Live only (the api answers 400 for a past date) and it overrides in_stock.
   */
  oos?: boolean;
};

export type InventorySummary = {
  /** Rows matching the filters. */
  lines: number;
  units_on_hand: number;
  /** On hand x average cost over the rows that have a cost. */
  value: number;
};

type ListInventoryResponse = {
  inventory: InventoryRow[];
  next_cursor: string | null;
  total_count: number | null;
  /** Totals over every matching row; first page only (null on a cursor fetch, absent on an older api). */
  summary?: InventorySummary | null;
  limit: number;
};

/** The filter part of the list and export query strings, shared so the export follows the screen. */
function appendInventoryFilters(p: URLSearchParams, f: InventoryFilters, date?: string): void {
  if (f.q) p.set('q', f.q);
  if (f.brand) p.set('brand', f.brand);
  if (f.warehouse) p.set('warehouse', f.warehouse);
  if (f.low_stock) p.set('low_stock', 'true');
  if (f.department) p.set('department', f.department);
  if (f.statement) p.set('statement', f.statement);
  if (f.oos) p.set('oos', 'true');
  // Only sent when explicitly off; the API defaults it on.
  if (f.in_stock === false) p.set('in_stock', 'false');
  // A past day reads from the inventory_snapshot table server-side instead of
  // the live mirrors; omitted for "today" so the default view is unchanged.
  if (date) p.set('date', date);
}

export function buildInventoryQs(f: InventoryFilters, cursor: string | null, date?: string): string {
  const p = new URLSearchParams();
  appendInventoryFilters(p, f, date);
  p.set('limit', '50');
  if (cursor) p.set('cursor', cursor);
  return p.toString();
}

/** Query string for the Excel export: the same filters as the grid, no paging. */
export function buildInventoryExportQs(f: InventoryFilters, date?: string): string {
  const p = new URLSearchParams();
  appendInventoryFilters(p, f, date);
  return p.toString();
}

/** Browser download of the inventory Excel export, following the filters and date on screen. */
export async function downloadCatalogInventoryXlsx(f: InventoryFilters, date?: string): Promise<void> {
  const qs = buildInventoryExportQs(f, date);
  const res = await fetch(`${CATALOG_API_BASE}/inventory/export${qs ? `?${qs}` : ''}`, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    // The api answers a request that is too large for one file with a plain sentence; show that, not the status line.
    let message = '';
    try {
      message = (JSON.parse(text) as { error?: { message?: string } }).error?.message ?? '';
    } catch {
      message = '';
    }
    throw new Error(message || `Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const m = (res.headers.get('content-disposition') ?? '').match(/filename="([^"]+)"/);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = m?.[1] ?? 'commerce-inventory.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

/**
 * `date`, when set to a day before today, asks the API to read the
 * inventory_snapshot table for that day instead of the live mirrors. Kept out
 * of `InventoryFilters` on purpose: the Subscribe control on the Inventory
 * page spreads `InventoryFilters` into a saved subscription, and a daily email
 * must always describe the current day, never a historical one.
 */
export function useCatalogInventory(filters: InventoryFilters = {}, date?: string) {
  const query = useInfiniteQuery<ListInventoryResponse, Error>({
    queryKey: ['catalog-inventory', filters, date],
    queryFn: ({ pageParam }) =>
      apiGet<ListInventoryResponse>(
        `${CATALOG_API_BASE}/inventory?${buildInventoryQs(filters, (pageParam as string | null) ?? null, date)}`,
      ),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 30_000,
    retry: false,
  });
  const rows: InventoryRow[] = (query.data?.pages ?? []).flatMap((p) => p.inventory);
  return {
    rows,
    totalCount: query.data?.pages[0]?.total_count ?? null,
    summary: query.data?.pages[0]?.summary ?? null,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: () => query.fetchNextPage(),
    error: query.error,
  };
}

type InventoryFilterOptionsResponse = { departments: string[]; statements: string[] };

/**
 * Department and Statement values for the Inventory filters, from the api pickers endpoint
 * (picker dropdowns over free text). Degrades to empty lists on 404/503 so the page still
 * renders while the api ships; the filters then simply offer nothing.
 */
export function useCatalogInventoryFilterOptions() {
  const query = useQuery<InventoryFilterOptionsResponse, Error>({
    queryKey: ['catalog-inventory-filter-options'],
    queryFn: () => apiGet<InventoryFilterOptionsResponse>(`${CATALOG_API_BASE}/inventory/filters`),
    staleTime: 10 * 60_000,
    retry: false,
  });
  return {
    departments: Array.isArray(query.data?.departments) ? query.data.departments : [],
    statements: Array.isArray(query.data?.statements) ? query.data.statements : [],
    isLoading: query.isLoading,
    available: !query.isLoading && !query.error,
  };
}

type SnapshotDatesResponse = { dates: string[] };

/**
 * Distinct `snapshot_date` values the api actually holds in inventory_snapshot,
 * so the Inventory date picker can bound itself to days that have real data
 * (history starts 8/2, and the nightly job can miss a day). Degrades to
 * `available: false` on 404/503/shape mismatch, matching `useCatalogLocations`:
 * the picker falls back to an unbounded min rather than the page crashing
 * while the api ships this endpoint in parallel.
 */
export function useCatalogInventorySnapshotDates() {
  const query = useQuery<SnapshotDatesResponse, Error>({
    queryKey: ['catalog-inventory-snapshot-dates'],
    queryFn: () => apiGet<SnapshotDatesResponse>(`${CATALOG_API_BASE}/inventory/snapshot-dates`),
    staleTime: 10 * 60_000,
    retry: false,
  });
  const dates = Array.isArray(query.data?.dates) ? query.data.dates : [];
  return {
    dates,
    earliestDate: dates.length ? dates.reduce((min, d) => (d < min ? d : min)) : null,
    isLoading: query.isLoading,
    available: !query.isLoading && !query.error,
  };
}

// -------- formatting helpers -------------------------------------------------

export function labelCatalogBrand(v: string | null | undefined): string {
  if (!v) return '--';
  switch (v.trim().toLowerCase()) {
    case 'hammonds':
      return "Hammond's";
    case 'lolli':
      return 'Lolli & Pops';
    default:
      return v;
  }
}

// Pill tones per brand so the two brands read distinctly in the grid.
export function catalogBrandTone(v: string | null | undefined): string {
  switch ((v ?? '').trim().toLowerCase()) {
    case 'hammonds':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
    case 'lolli':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300';
  }
}

// Money from a decimal string (the API returns DECIMAL as a string).
export function formatCatalogMoney(v: string | number | null | undefined): string {
  if (v == null) return '--';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '--';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

// Average cost per unit from a decimal string: dollars to four places, blank dash when the brand has none.
export function formatCatalogCost(v: string | number | null | undefined): string {
  if (v == null) return '--';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '--';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(n);
}

// Quantity from a decimal string: whole units when integral, else up to 2dp.
export function formatCatalogQty(v: string | number | null | undefined): string {
  if (v == null) return '--';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '--';
  if (Number.isInteger(n)) return n.toLocaleString('en-US');
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

// Warehouse label: "101 · Tulsa" when the name is present, otherwise the bare
// code. Tolerant of an absent/blank name so it degrades to code-only.
export function formatWarehouse(code: string, name: string | null | undefined): string {
  const trimmed = name?.trim();
  return trimmed ? `${code} · ${trimmed}` : code;
}

export const CATALOG_BRAND_OPTIONS: { value: CatalogBrand; label: string }[] = [
  { value: 'hammonds', label: "Hammond's" },
  { value: 'lolli', label: 'Lolli & Pops' },
];

// -------- location pickers ---------------------------------------------------

export type CatalogLocation = {
  brand: string;
  code: string;
  /**
   * Store name for Lolli. ALWAYS null for Hammond's: the mirrored Sage data has
   * no warehouse master (only IM_ItemWarehouse), so there is no warehouse name to
   * show and the UI falls back to the bare code.
   */
  name: string | null;
};

type ListLocationsResponse = { locations: CatalogLocation[] };

/**
 * Options for the Inventory Warehouse picker. Brand-aware: pass the active brand
 * filter so the dropdown only offers that brand's namespace (Hammond's 3-digit
 * warehouse codes and Lolli's 4-digit store numbers are unrelated).
 *
 * Long staleTime because the location list changes when a store opens or closes,
 * not during a session.
 */
export function useCatalogLocations(brand?: CatalogBrand) {
  const query = useQuery<ListLocationsResponse, Error>({
    queryKey: ['catalog-locations', brand ?? 'all'],
    queryFn: () =>
      apiGet<ListLocationsResponse>(`${CATALOG_API_BASE}/locations${brand ? `?brand=${brand}` : ''}`),
    staleTime: 10 * 60_000,
    retry: false,
  });
  return {
    locations: query.data?.locations ?? [],
    isLoading: query.isLoading,
    // Degrades to an empty list on 404/503 so the filter can fall back rather
    // than the page crashing while the api ships in parallel.
    available: !query.isLoading && !query.error,
    error: query.error,
  };
}

/** Trigger/option label: "060" when there is no name, "1112 - Tulsa" when there is. */
export function formatLocationLabel(l: CatalogLocation): string {
  const name = l.name?.trim();
  return name ? `${l.code} - ${name}` : l.code;
}
